import { useState, useEffect, useContext } from "react";
import AuthContext from "../../../../../utils/secure-route/AuthContext";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../../common/ToastHandler";
import ProductCategoryTable from "./ProductCategoryTable";
import ReusableModal from "../../../AppPAges/ManageSettings/ReusableModal";
import { ReusableInput } from "../../../../common/ReusableComponent/ReusableInput";
import { CustomButton, RegularButton } from "../../../../common/ReusableComponent/CustomButton";
import useSwal from "../../../../common/ReusableComponent/useSwal";
import useMyPermissions from "../../../../../utils/permissions/useMyPermissions";
import Can from "../../../../../utils/permissions/Can";

const ProductCategory = () => {
    const { user } = useContext(AuthContext);
    const storeId = user.store;

    const { GetPaginatedData, Post, Patch, Delete, Get, getAPI, getHost } = useApi();
    const { handleToast } = useToastHandler();
    const { confirmDelete } = useSwal();
    const { can } = useMyPermissions();

    const [data, setData] = useState([]);
    const [previewData, setPreviewData] = useState([]);

    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);

    const [chain, setChain] = useState([]);
    const [input, setInput] = useState("");
    const [childPool, setChildPool] = useState([]);
    const [breadcrumbPool, setBreadcrumbPool] = useState([]);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [ddOptions, setDdOptions] = useState([]);
    const [ddPage, setDdPage] = useState(1);
    const [ddHasMore, setDdHasMore] = useState(true);
    const [ddLoading, setDdLoading] = useState(false);
    const [ddSearch, setDdSearch] = useState("");
    const [ddDebounced, setDdDebounced] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const [nextPage, setNextPage] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);

    /* ---------------- FETCH (initial / search reset) ---------------- */

    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;
    const fetchData = async ({ page = 1, search = "", append = false } = {}) => {
        try {
            append ? setLoadingMore(true) : setLoading(true);

            const res = await GetPaginatedData("productCategory", {
                page,
                search: search || undefined,
            });

            const incoming = res?.data || [];

            setData(prev => append ? [...prev, ...incoming] : incoming);

            setNextPage(res?.nextPage || null);

            // 🔥 REQUIRED
            setCurrentPage(page);

        } catch {
            handleToast({ type: "ERROR", message: "Fetch failed" });
        } finally {
            append ? setLoadingMore(false) : setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ page: 1, search: searchTerm });
    }, []);

    /* ---------------- LOAD MORE on scroll ---------------- */
    const handleLoadMore = () => {
        if (!nextPage || loadingMore) return;
        const url = new URL(nextPage);
        const page = url.searchParams.get("page") || 2;
        fetchData({ page: Number(page), search: searchTerm, append: true });
    };

    /* ---------------- SEARCH ---------------- */
    const handleSearch = (term) => {
        setSearchTerm(term);
        if (!term.trim()) {
            fetchData({ page: 1, search: "" });
            return;
        }
        // Use the search param on the main endpoint — returns results with ancestors
        fetchData({ page: 1, search: term });
    };

    /* ---------------- CREATE / UPDATE ---------------- */
    const handleCreate = async () => {
        if (!input.trim()) {
            handleToast({ type: "ERROR", message: "Name required" });
            return;
        }

        const parentId = chain.length ? chain[chain.length - 1].id : null;
        const payload = { name: input, parent: parentId, store: storeId };

        try {
            if (editItem) {
                await Patch("productCategory", editItem.id, payload);
                handleToast({ type: "SUCCESS", message: "Updated" });
                handleClose();
            } else {
                const res = await Post("productCategory", payload);
                const created = res?.data;
                handleToast({ type: "SUCCESS", message: "Created" });
                setPreviewData(prev => [...prev, { id: created.id, name: created.name, parent: parentId }]);
                setInput("");
            }
            fetchData({ page: 1, search: searchTerm });
        } catch {
            handleToast({ type: "ERROR", message: editItem ? "Update failed" : "Create failed" });
        }
    };

    /* ---------------- EDIT ---------------- */
    const handleEdit = (item) => {
        setEditItem(item);
        setInput(item.name);
        if (item.parent) {
            setChain([{ id: item.parent, name: item.parent_name || "" }]);
            setPreviewData([{ id: item.id, name: item.name, parent: item.parent }]);
        } else {
            setChain([]);
            setPreviewData([{ id: item.id, name: item.name, parent: null }]);
        }
        setShowModal(true);
    };

    /* ---------------- DELETE ---------------- */
    const handleDelete = async (id) => {
        if (!id) return;

        const result = await confirmDelete();
        if (!result?.isConfirmed) return;

        try {
            await Delete("productCategory", id);
            handleToast({ type: "SUCCESS", message: "Deleted" });
            fetchData({ page: 1, search: searchTerm });
        } catch {
            handleToast({ type: "ERROR", message: "Delete failed" });
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setEditItem(null);
        setChain([]);
        setInput("");
        setPreviewData([]);
        setChildPool([]);
        setBreadcrumbPool([]);
        setDropdownOpen(false);
        setDdOptions([]);
        setDdPage(1);
        setDdHasMore(true);
        setDdSearch("");
        setDdDebounced("");
    };

    /* ---------------- DROPDOWN FETCH ---------------- */
    const fetchDdOptions = async ({ page = 1, search = "", reset = false } = {}) => {
        if (ddLoading) return;
        try {
            setDdLoading(true);
            const res = await GetPaginatedData("productCategory", { page, search: search || undefined });
            const items = res?.data || [];
            setDdOptions(prev => reset ? items : [...prev, ...items]);
            setDdHasMore(!!res?.nextPage);
            setDdPage(page);
        } catch {
            // silent
        } finally {
            setDdLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => setDdDebounced(ddSearch), 400);
        return () => clearTimeout(t);
    }, [ddSearch]);

    useEffect(() => {
        if (dropdownOpen) fetchDdOptions({ page: 1, search: ddDebounced, reset: true });
    }, [ddDebounced, dropdownOpen]);

    const handleDdScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 40 && ddHasMore && !ddLoading) {
            fetchDdOptions({ page: ddPage + 1, search: ddDebounced });
        }
    };

    /* ---------------- NORMALIZE URL HOST ---------------- */
    const normalizeUrl = (url) => {
        try {
            const parsed = new URL(url);
            const host = getHost();
            const hostParsed = new URL(host);
            parsed.protocol = hostParsed.protocol;
            parsed.host = hostParsed.host;
            return parsed.toString();
        } catch {
            return url;
        }
    };

    /* ---------------- FETCH CHILDREN BY SLUG ---------------- */
    const fetchChildren = async (slug) => {
        try {
            const url = normalizeUrl(getAPI("categoryChild") + slug);
            const res = await Get(url);
            const subCategories = res?.sub_categories || [];
            const breadcrumb = res?.breadcrumb || [];
            setChildPool(subCategories);
            // store breadcrumb so preview can resolve full ancestor chain
            setBreadcrumbPool(prev => {
                const existingIds = new Set(prev.map(b => b.id));
                const newOnes = breadcrumb.filter(b => !existingIds.has(b.id));
                return [...prev, ...newOnes];
            });
        } catch {
            setChildPool([]);
        }
    };

    /* ---------------- FLATTEN ALL (for dropdown) ---------------- */
    const flattenAll = (items) => {
        const result = [];
        const map = {};
        const roots = [];

        items.forEach(i => { map[i.id] = { ...i, children: [] }; });
        items.forEach(i => {
            if (i.parent && map[i.parent]) map[i.parent].children.push(map[i.id]);
            else roots.push(map[i.id]);
        });

        const traverse = (node, d) => {
            result.push({ id: node.id, name: node.name, slug: node.slug, parent: node.parent ?? null, depth: d });
            node.children.forEach(c => traverse(c, d + 1));
        };
        roots.forEach(r => traverse(r, 0));
        return result;
    };

    /* ---------------- TREE ---------------- */
    const buildTree = (items) => {
        const map = {};
        const roots = [];

        items.forEach(i => {
            map[i.id] = { ...i, children: [] };
        });

        items.forEach(i => {
            if (i.parent) {
                map[i.parent]?.children.push(map[i.id]);
            } else {
                roots.push(map[i.id]);
            }
        });

        return roots;
    };

    const TreeNode = ({ node, prefix = "", isLast = true }) => (
        <div style={{ fontFamily: "monospace", fontSize: "13px", color: "#333", lineHeight: "1.9", whiteSpace: "pre" }}>
            <div>{prefix}{isLast ? "└── " : "├── "}{node.name}</div>
            {node.children?.map((child, i) => (
                <TreeNode
                    key={child.id}
                    node={child}
                    prefix={prefix + (isLast ? "    " : "│   ")}
                    isLast={i === node.children.length - 1}
                />
            ))}
        </div>
    );

    return (
        <div className="responsive-box">
            <div className="inventory-header">
                <div className="inventory-title">Product Category</div>
                <Can module="productService" action="add">
                    <CustomButton text="Add Category" onClick={() => setShowModal(true)} />
                </Can>
            </div>

            <ProductCategoryTable
                data={data}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={!!nextPage}
                onEdit={can("productService", "change") ? handleEdit : undefined}
                onDelete={can("productService", "delete") ? handleDelete : undefined}
                onLoadMore={handleLoadMore}
                onSearch={handleSearch}
                currentPage={currentPage}        // ✅ REQUIRED
                nextPage={nextPage}              // ✅ REQUIRED
                onPageChange={(page) => fetchData({ page, search: searchTerm })}
            />

            <ReusableModal
                title={editItem ? "Edit Category" : "Create Category"}
                show={showModal}
                handleClose={handleClose}
                width="500px"
            >
                <div style={{ padding: "10px 20px 20px" }}>

                    {/* PARENT SELECT — searchable scrollable dropdown */}
                    {!(editItem && !editItem.parent) && (
                        <div style={{ marginBottom: "14px", position: "relative" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#555", marginBottom: "5px", display: "block" }}>Parent Category</label>
                            <input
                                type="text"
                                placeholder={chain.length ? chain[chain.length - 1].name : "Search or select parent…"}
                                value={ddSearch}
                                onFocus={() => setDropdownOpen(true)}
                                onChange={(e) => { setDdSearch(e.target.value); setDropdownOpen(true); }}
                                style={{
                                    width: "100%",
                                    padding: "8px 12px",
                                    borderRadius: "8px",
                                    border: "1px solid #ddd",
                                    fontSize: "13px",
                                    color: "#333",
                                    background: "#fafafa",
                                    outline: "none",
                                    boxSizing: "border-box"
                                }}
                            />
                            {dropdownOpen && (
                                <div
                                    onScroll={handleDdScroll}
                                    style={{
                                        position: "absolute",
                                        top: "100%",
                                        left: 0,
                                        right: 0,
                                        background: "#fff",
                                        border: "1px solid #ddd",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                        zIndex: 999,
                                        maxHeight: "220px",
                                        overflowY: "auto"
                                    }}
                                >
                                    {/* Root option */}
                                    <div
                                        onMouseDown={() => {
                                            setChain([]); setChildPool([]); setBreadcrumbPool([]);
                                            setDdSearch(""); setDropdownOpen(false);
                                        }}
                                        style={{ padding: "8px 14px", fontSize: "13px", color: "#888", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
                                        onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                                        onMouseLeave={e => e.currentTarget.style.background = ""}
                                    >
                                        Root (No Parent)
                                    </div>
                                    {flattenAll(ddOptions).map(opt => (
                                        <div
                                            key={opt.id}
                                            onMouseDown={() => {
                                                setChain([{ id: opt.id, name: opt.name }]);
                                                if (opt.slug) fetchChildren(opt.slug);
                                                setDdSearch(""); setDropdownOpen(false);
                                            }}
                                            style={{
                                                padding: "8px 14px",
                                                paddingLeft: `${14 + opt.depth * 14}px`,
                                                fontSize: "13px",
                                                color: "#333",
                                                cursor: "pointer",
                                                borderBottom: "1px solid #f5f5f5"
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                                            onMouseLeave={e => e.currentTarget.style.background = ""}
                                        >
                                            {opt.depth > 0 ? "└ " : ""}{opt.name}
                                        </div>
                                    ))}
                                    {ddLoading && (
                                        <div style={{ padding: "8px 14px", fontSize: "12px", color: "#aaa", textAlign: "center" }}>Loading…</div>
                                    )}
                                    {!ddLoading && ddOptions.length === 0 && (
                                        <div style={{ padding: "10px 14px", fontSize: "13px", color: "#aaa" }}>No results</div>
                                    )}
                                </div>
                            )}
                            {dropdownOpen && (
                                <div
                                    style={{ position: "fixed", inset: 0, zIndex: 998 }}
                                    onMouseDown={() => setDropdownOpen(false)}
                                />
                            )}
                        </div>
                    )}

                    {/* CURRENT PARENT BADGE */}
                    {chain.length > 0 && (
                        <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "12px", color: "#888" }}>Under:</span>
                            <span style={{
                                background: "#e8f0fe",
                                color: "#3b5bdb",
                                borderRadius: "20px",
                                padding: "3px 12px",
                                fontSize: "12px",
                                fontWeight: "600"
                            }}>
                                {chain[chain.length - 1].name}
                            </span>
                        </div>
                    )}

                    {/* INPUT */}
                    <ReusableInput
                        name="name"
                        label={chain.length ? "Child Category Name" : "Category Name"}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                    />

                    {/* BUTTONS */}
                    <div className="d-flex gap-2 mt-3">
                        <RegularButton text={editItem ? "Update" : "Save"} onClick={handleCreate} />
                        <RegularButton
                            text="Reset Parent"
                            onClick={() => setChain([])}
                            backgroundColor="#eee"
                            color="#000"
                        />
                    </div>

                    {/* LIVE TREE PREVIEW */}
                    {(chain.length > 0 || previewData.length > 0) && (() => {
                        // get the selected parent's full ancestor chain from breadcrumbPool
                        const breadcrumbIds = breadcrumbPool.map(b => b.id);
                        const selectedParentId = chain.length ? chain[chain.length - 1].id : null;

                        // collect only the relevant branch: breadcrumb nodes + selected parent + saved preview items
                        const allKnown = [...data, ...childPool, ...ddOptions];
                        const branchIds = new Set(breadcrumbIds);
                        if (selectedParentId) branchIds.add(selectedParentId);
                        previewData.forEach(p => branchIds.add(p.id));

                        // build items with correct parent links using data as source of truth
                        const liveItems = [];
                        const seen = new Set();

                        branchIds.forEach(id => {
                            const found = allKnown.find(d => d.id === id);
                            if (found && !seen.has(id)) {
                                seen.add(id);
                                liveItems.push({ id: found.id, name: found.name, parent: found.parent ?? null });
                            }
                        });

                        // add saved preview items
                        previewData.forEach(p => {
                            if (!seen.has(p.id)) {
                                seen.add(p.id);
                                liveItems.push(p);
                            }
                        });

                        // ghost node for live typing (create mode only)
                        if (input.trim() && !editItem) {
                            liveItems.push({
                                id: "__ghost__",
                                name: input + " ✏️",
                                parent: selectedParentId
                            });
                        }

                        // in edit mode, reflect the renamed item live
                        if (editItem && input.trim()) {
                            const idx = liveItems.findIndex(i => i.id === editItem.id);
                            if (idx !== -1) liveItems[idx] = { ...liveItems[idx], name: input + " ✏️" };
                        }

                        return (
                            <div style={{ marginTop: "18px", background: "#fafafa", border: "1px solid #eee", borderRadius: "10px", padding: "12px" }}>
                                <div style={{ fontSize: "12px", fontWeight: "700", color: "#555", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Preview</div>
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    {buildTree(liveItems).map(node => (
                                        <TreeNode key={node.id} node={node} />
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </ReusableModal>
        </div>
    );
};

export default ProductCategory;